import { useState } from 'react';
import { getDefaultExpandedState } from './FilterSidebar.helpers';

export type FilterSidebarExpandedState = {
  [key: string]: boolean;
};

export type FilterSidebarShowMoreState = {
  [key: string]: boolean;
};

export const useFilterSidebarState = (
  hierarchyStateKeys: string[],
  valueCategoriesCollapsedByDefault: boolean,
) => {
  const [expandedSections, setExpandedSections] =
    useState<FilterSidebarExpandedState>({
      filters: true,
      type: true,
      agency: true,
      topicCat: true,
      orgName: true,
      format: true,
    });

  const [showMoreStates, setShowMoreStates] =
    useState<FilterSidebarShowMoreState>({});

  const isHierarchyExpanded =
    hierarchyStateKeys.length > 0 &&
    hierarchyStateKeys.every(
      (stateKey) =>
        expandedSections[stateKey] ??
        getDefaultExpandedState(stateKey, valueCategoriesCollapsedByDefault),
    );

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !(
        prev[section] ??
        getDefaultExpandedState(section, valueCategoriesCollapsedByDefault)
      ),
    }));
  };

  const toggleShowMore = (section: string) => {
    setShowMoreStates((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleHierarchy = () => {
    if (!hierarchyStateKeys.length) {
      return;
    }

    setExpandedSections((prev) => {
      const shouldCollapse = hierarchyStateKeys.every(
        (stateKey) =>
          prev[stateKey] ??
          getDefaultExpandedState(stateKey, valueCategoriesCollapsedByDefault),
      );

      return hierarchyStateKeys.reduce(
        (nextState, stateKey) => ({
          ...nextState,
          [stateKey]: !shouldCollapse,
        }),
        { ...prev },
      );
    });
  };

  return {
    expandedSections,
    showMoreStates,
    isHierarchyExpanded,
    toggleSection,
    toggleShowMore,
    toggleHierarchy,
  };
};
